<template>
  <div class="ca-filters">
    <div class="ca-filters__field">
      <label class="ca-filters__label">{{ $t('cleaner') }}</label>
      <Select
        class="ca-filters__select form-select"
        mdClass="ca-filters-menu"
        :value="value.cleanerIds"
        :multiple="true"
        :disabled="disabled"
        :placeholder="$t('all')"
        @input="updateField('cleanerIds', $event)"
      >
        <Option
          v-for="cleaner in cleanerOptions"
          :key="cleaner.id"
          :value="cleaner.id"
          :label="cleaner.label"
        />
      </Select>
    </div>

    <div class="ca-filters__field">
      <label class="ca-filters__label">{{ $t('working_area') }}</label>
      <Select
        class="ca-filters__select form-select"
        mdClass="ca-filters-menu"
        :value="value.workingAreaIds"
        :multiple="true"
        :disabled="disabled"
        :placeholder="$t('all')"
        @input="updateField('workingAreaIds', $event)"
      >
        <Option
          v-for="area in workingAreas"
          :key="area.id"
          :value="area.id"
          :label="area.area_name"
        />
      </Select>
    </div>

    <div class="ca-filters__field">
      <label class="ca-filters__label">{{ $t('rooms') }}</label>
      <Select
        class="ca-filters__select form-select"
        mdClass="ca-filters-menu"
        :value="value.roomIds"
        :multiple="true"
        :disabled="disabled"
        :placeholder="$t('all')"
        @input="updateField('roomIds', $event)"
      >
        <Option
          v-for="room in roomOptions"
          :key="room.id"
          :value="room.id"
          :label="room.name"
        />
      </Select>
    </div>

    <div class="ca-filters__field">
      <label class="ca-filters__label">{{ $t('ca_sort_label') }}</label>
      <Select
        class="ca-filters__select form-select"
        mdClass="ca-filters-menu"
        :value="value.sortKey || defaultSortKey"
        :multiple="false"
        :disabled="disabled"
        :placeholder="$t('ca_sort_label')"
        @input="updateSortKey"
      >
        <Option
          v-for="sortKey in sortOptions"
          :key="sortKey"
          :value="sortKey"
          :label="$t(sortLabelKey(sortKey))"
        />
      </Select>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Select from '../../../common/components/Select'
import Option from '../../../common/components/Option'
import {
  SORT_OPTIONS,
  DEFAULT_SORT_KEY,
} from './cleanerActivityFilter'

const SORT_LABEL_KEYS = {
  needs_attention: 'ca_sort_needs_attention',
  best_avg_response: 'ca_sort_best_avg_response',
  worst_avg_response: 'ca_sort_worst_avg_response',
  most_sla_breaches: 'ca_sort_most_sla_breaches',
  most_open_complaints: 'ca_sort_most_open_complaints',
  most_work_time: 'ca_sort_most_work_time',
  least_work_time: 'ca_sort_least_work_time',
}

/** Fields of the Filters popover on the Shift view (the popover chrome lives in CleanerActivityFilterMenu). */
export default {
  components: { Select, Option },
  props: {
    value: {
      type: Object,
      required: true,
    },
    workingAreas: {
      type: Array,
      default: () => [],
    },
    cleaners: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters(['filterOptions']),
    sortOptions() {
      return SORT_OPTIONS
    },
    defaultSortKey() {
      return DEFAULT_SORT_KEY
    },
    cleanerOptions() {
      return (this.cleaners || [])
        .map((cleaner) => ({
          id: cleaner.employeeId,
          label: cleaner.username || cleaner.phone || `#${cleaner.employeeId}`,
        }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label)))
    },
    roomOptions() {
      const rooms = (this.filterOptions && this.filterOptions.rooms) || []
      return rooms
        .filter((room) => room.is_calculated === 1 || room.is_calculated == null)
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    },
  },
  methods: {
    sortLabelKey(sortKey) {
      return SORT_LABEL_KEYS[sortKey] || 'ca_sort_needs_attention'
    },
    updateField(field, nextValue) {
      const normalized = Array.isArray(nextValue) ? nextValue : (nextValue != null ? [nextValue] : [])
      this.$emit('input', { ...this.value, [field]: normalized })
    },
    updateSortKey(nextValue) {
      const sortKey = Array.isArray(nextValue) ? nextValue[0] : nextValue
      this.$emit('input', {
        ...this.value,
        sortKey: sortKey || DEFAULT_SORT_KEY,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@use "./caControls" as *;

.ca-filters {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__label {
    display: block;
    margin-bottom: 4px;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #7f8c8d;
  }

  :deep(.ca-filters__select.select-container.md-field) {
    @include ca-compact-select;
  }
}
</style>

<style lang="scss">
/* Dropdown teleported to body — scoped styles cannot reach it */
.ca-filters-menu.md-menu-content {
  max-height: 280px !important;

  .md-list-item-content {
    min-height: 36px;
    font-size: 0.8125rem;
  }
}
</style>
